/**
 * LabReportPDF.js — uses react-to-print (browser print engine).
 * Usage: wrap <PrintableLabReport ref={printRef} ... /> in your component,
 *        then call handlePrint() from useReactToPrint({ contentRef: printRef }).
 */
import React from "react";
import ReactMarkdown from "react-markdown";

const NAVY      = "#0a0e27";
const GOLD      = "#637cdb";
const GRAY      = "#64748b";
const TEXT_DARK = "#1e293b";
const HIGH_MARK = "#8b5a2b";   // muted amber-brown — elevated
const LOW_MARK  = "#1a5276";   // muted steel blue — below range
const GREEN     = "#16a34a";

// ── Markdown components ───────────────────────────────────────────────────────
const mdPrint = {
  h1: ({ children }) => (
    <div style={{
      background: NAVY, color: "#fff",
      fontSize: 15, fontWeight: 800, letterSpacing: "0.03em",
      padding: "8px 14px", marginTop: 24, marginBottom: 10,
      borderRadius: 3, borderLeft: `4px solid ${GOLD}`,
    }}>{children}</div>
  ),
  h2: ({ children }) => (
    <div style={{
      fontSize: 13, fontWeight: 700, color: NAVY,
      marginTop: 18, marginBottom: 6,
      background: "#edf2fc",
      padding: "6px 12px",
      borderLeft: `4px solid ${GOLD}`,
      borderRadius: "0 3px 3px 0",
    }}>{children}</div>
  ),
  h3: ({ children }) => (
    <div style={{
      fontSize: 11.5, fontWeight: 700, color: NAVY,
      marginTop: 13, marginBottom: 4,
      borderBottom: `1px solid #d0ddf5`,
      paddingBottom: 3,
    }}>{children}</div>
  ),
  p: ({ children }) => (
    <p style={{ fontSize: 10.5, color: "#1e293b", lineHeight: 1.85, marginBottom: 7, marginTop: 0 }}>{children}</p>
  ),
  ul: ({ children }) => <ul style={{ paddingLeft: 16, marginBottom: 7, marginTop: 0 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ paddingLeft: 16, marginBottom: 7, marginTop: 0 }}>{children}</ol>,
  li: ({ children }) => {
    const text = typeof children === "string" ? children :
      Array.isArray(children) ? children.map(c => typeof c === "string" ? c : "").join("") : "";
    const isHigh = /\(high\)|\bH\b|↑/i.test(text);
    const isLow  = /\(low\)|\bL\b|↓/i.test(text);
    const accentColor = isHigh ? HIGH_MARK : isLow ? LOW_MARK : "transparent";
    return (
      <li style={{
        fontSize: 10.5, color: TEXT_DARK, lineHeight: 1.85, marginBottom: 3,
        borderLeft: accentColor !== "transparent" ? `3px solid ${accentColor}` : "none",
        paddingLeft: accentColor !== "transparent" ? 6 : 0,
        listStyle: accentColor !== "transparent" ? "none" : "disc",
        marginLeft: accentColor !== "transparent" ? -8 : 0,
      }}>{children}</li>
    );
  },
  strong: ({ children }) => <strong style={{ fontWeight: 700, color: "#0f172a" }}>{children}</strong>,
  em:     ({ children }) => <em style={{ fontStyle: "italic", color: GRAY }}>{children}</em>,
  hr:     () => <hr style={{ border: "none", borderTop: `1px solid ${GOLD}`, margin: "14px 0" }} />,
  blockquote: ({ children }) => (
    <blockquote style={{ borderLeft: `3px solid ${GRAY}`, paddingLeft: 12, color: GRAY,
      fontStyle: "italic", margin: "8px 0", fontSize: 10 }}>{children}</blockquote>
  ),
};

// ── Status helpers ────────────────────────────────────────────────────────────
function statusAccent(status) {
  return status === "High" ? HIGH_MARK : status === "Low" ? LOW_MARK : GRAY;
}
function statusSymbol(status) {
  return status === "High" ? "↑" : status === "Low" ? "↓" : "—";
}

// ── Parse reference range string into {low, high} ────────────────────────────
function parseRef(ref) {
  if (!ref) return null;
  const range = ref.match(/([0-9.]+)\s*[-–]\s*([0-9.]+)/);
  if (range) return { low: parseFloat(range[1]), high: parseFloat(range[2]) };
  const lt = ref.match(/<\s*([0-9.]+)/);
  if (lt) return { low: null, high: parseFloat(lt[1]) };
  const gt = ref.match(/>\s*([0-9.]+)/);
  if (gt) return { low: parseFloat(gt[1]), high: null };
  return null;
}

// ── SVG sparkline chart ───────────────────────────────────────────────────────
function Sparkline({ pts, ref: refStr }) {
  const W = 200, H = 48;

  const numericPts = pts.map(p => ({ ...p, num: parseFloat(p.value) })).filter(p => !isNaN(p.num));
  if (numericPts.length < 2) return null;

  const parsed = parseRef(refStr);
  const allVals = numericPts.map(p => p.num);
  if (parsed?.low  != null) allVals.push(parsed.low);
  if (parsed?.high != null) allVals.push(parsed.high);

  const minV = Math.min(...allVals);
  const maxV = Math.max(...allVals);
  const span = maxV - minV || 1;
  const PAD  = 4;

  const toX = i => PAD + (i / (numericPts.length - 1)) * (W - PAD * 2);
  const toY = v => PAD + ((maxV - v) / span) * (H - PAD * 2);

  const linePts = numericPts.map((p, i) => `${toX(i)},${toY(p.num)}`).join(" ");

  return (
    <svg width={W} height={H} style={{ display: "block", overflow: "visible" }}>
      {/* Reference range band */}
      {parsed?.low != null && parsed?.high != null && (
        <rect
          x={PAD} y={toY(parsed.high)}
          width={W - PAD * 2} height={toY(parsed.low) - toY(parsed.high)}
          fill="rgba(22,163,74,0.1)" stroke="none"
        />
      )}
      {parsed?.high != null && (
        <line x1={PAD} y1={toY(parsed.high)} x2={W - PAD} y2={toY(parsed.high)}
          stroke="rgba(22,163,74,0.45)" strokeWidth={0.8} strokeDasharray="3,2" />
      )}
      {parsed?.low != null && (
        <line x1={PAD} y1={toY(parsed.low)} x2={W - PAD} y2={toY(parsed.low)}
          stroke="rgba(22,163,74,0.45)" strokeWidth={0.8} strokeDasharray="3,2" />
      )}

      {/* Trend line */}
      <polyline points={linePts} fill="none" stroke={NAVY} strokeWidth={1.6}
        strokeLinejoin="round" strokeLinecap="round" />

      {/* Data point dots */}
      {numericPts.map((p, i) => {
        const color = p.status === "High" ? HIGH_MARK : p.status === "Low" ? LOW_MARK : GREEN;
        return (
          <circle key={i} cx={toX(i)} cy={toY(p.num)} r={3.2}
            fill={color} stroke="white" strokeWidth={1.2} />
        );
      })}
    </svg>
  );
}

// ── Trend row with sparkline ──────────────────────────────────────────────────
function TrendRow({ chart }) {
  const pts  = chart.points;
  const last = pts[pts.length - 1];
  const accent = statusAccent(last.status);

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, marginBottom: 10,
      padding: "8px 10px", border: `1px solid #e2e6f0`,
      borderLeft: `4px solid ${accent}`, borderRadius: 4, background: "#fafafa",
    }}>
      {/* Test name + unit + dates column */}
      <div style={{ flex: "0 0 130px" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: NAVY,
          overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", marginBottom: 3 }}>
          {chart.test}
          <span style={{ fontSize: 8, color: GRAY, fontWeight: 400, marginLeft: 4 }}>{chart.unit}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {pts.map((p, i) => (
            <div key={i} style={{ fontSize: 8, color: GRAY, display: "flex", justifyContent: "space-between" }}>
              <span>{p.date_label}</span>
              <span style={{ color: statusAccent(p.status), fontWeight: 700, marginLeft: 6 }}>
                {p.value}{statusSymbol(p.status)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Sparkline */}
      <div style={{ flex: "0 0 200px" }}>
        <Sparkline pts={pts} ref={chart.ref} />
      </div>

      {/* Status badge */}
      <div style={{
        fontSize: 9, fontWeight: 700, color: accent,
        border: `1px solid ${accent}`, borderRadius: 3,
        padding: "2px 6px", whiteSpace: "nowrap", marginLeft: "auto",
      }}>
        {statusSymbol(last.status)} {last.status.toUpperCase()}
      </div>
    </div>
  );
}

// ── Printable report component ────────────────────────────────────────────────
export const PrintableLabReport = React.forwardRef(
  ({ answer, metadata, botLabel, trendsData }, ref) => {
    const validMeta = (metadata || []).filter(m => m.field && m.value && m.value !== "Unknown");
    const charts    = trendsData?.charts || [];

    return (
      <div ref={ref}>
        <style>{`
          @media print {
            @page { margin: 30px 40px; size: A4; }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        `}</style>

        <div style={{ fontFamily: "'Metropolis', Arial, sans-serif", maxWidth: 760, margin: "0 auto", padding: 32 }}>

          {/* ── Header ─────────────────────────────────────────────────────── */}
          <div style={{ background: NAVY, color: "#fff", padding: "14px 20px",
            marginBottom: 0, borderRadius: "4px 4px 0 0",
            display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <img
                src="/logo-light.png"
                alt="Proove Genomics"
                style={{ height: 30, width: "auto", display: "block", marginBottom: 6 }}
                onError={e => { e.target.style.display = "none"; }}
              />
              <div style={{ fontSize: 10, color: GOLD, fontFamily: "'Michroma', Arial, sans-serif" }}>
                {botLabel.toUpperCase()} AI ANALYSIS REPORT &nbsp;·&nbsp; {new Date().toLocaleString()}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginBottom: 2, fontFamily: "'Michroma', Arial, sans-serif" }}>
                PROOVE GENOMICS
              </div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.35)" }}>proove.com</div>
            </div>
          </div>
          <div style={{ height: 3, background: GOLD, marginBottom: 20 }} />

          {/* ── Metadata card ───────────────────────────────────────────────── */}
          {validMeta.length > 0 && (
            <div style={{ background: "#eef1f8", border: `1px solid ${GOLD}`,
              borderLeft: `4px solid ${NAVY}`, borderRadius: 4, padding: "12px 16px", marginBottom: 20 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: NAVY, letterSpacing: "0.1em",
                marginBottom: 8 }}>PATIENT INFORMATION</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 24px" }}>
                {validMeta.map((m, i) => (
                  <div key={i} style={{ fontSize: 10, display: "flex", gap: 6 }}>
                    <span style={{ fontWeight: 700, color: GRAY, minWidth: 80 }}>
                      {m.field.toUpperCase()}:
                    </span>
                    <span style={{ color: "#1e293b" }}>{m.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── AI Analysis ─────────────────────────────────────────────────── */}
          <div style={{
            fontSize: 15, fontWeight: 800, color: NAVY, letterSpacing: "0.05em",
            marginBottom: 6, marginTop: 4,
            textTransform: "uppercase",
            fontFamily: "'Michroma', Arial, sans-serif",
          }}>
            {botLabel} AI Analysis
          </div>
          <div style={{ height: 2, background: `linear-gradient(to right, ${GOLD}, transparent)`, marginBottom: 16 }} />

          <ReactMarkdown components={mdPrint}>{answer || ""}</ReactMarkdown>

          {/* ── Trend charts ────────────────────────────────────────────────── */}
          {charts.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: NAVY, letterSpacing: "0.08em",
                marginBottom: 4, fontFamily: "'Michroma', Arial, sans-serif" }}>LONGITUDINAL TREND CHARTS</div>
              <div style={{ height: 1, background: GOLD, marginBottom: 8 }} />
              <div style={{ fontSize: 9, color: GRAY, marginBottom: 12 }}>
                Showing {charts.length} key biomarker{charts.length !== 1 ? "s" : ""} across{" "}
                {trendsData.visit_count} saved visit{trendsData.visit_count !== 1 ? "s" : ""},
                ordered chronologically. &nbsp;
                <span style={{ color: GREEN }}>—— Normal range</span> &nbsp;
                <span style={{ color: HIGH_MARK }}>↑ Above range</span> &nbsp;
                <span style={{ color: LOW_MARK }}>↓ Below range</span>
              </div>
              {charts.map((c, i) => <TrendRow key={i} chart={c} />)}
            </div>
          )}

          {/* ── Disclaimer ─────────────────────────────────────────────────── */}
          <div style={{ marginTop: 32, borderTop: `1px solid #e2e6f0`, paddingTop: 12,
            fontSize: 8.5, color: GRAY, textAlign: "center", lineHeight: 1.6 }}>
            This AI-generated report is produced by Proove Genomics' {botLabel} intelligence tool
            for informational purposes only and does not constitute medical advice. Consult a licensed
            healthcare provider before making any medical decisions.
            &nbsp;·&nbsp; proove.com &nbsp;·&nbsp; Generated {new Date().toLocaleDateString()}
          </div>
        </div>
      </div>
    );
  }
);
PrintableLabReport.displayName = "PrintableLabReport";
